import { useState } from 'react'
import { useSettingsStore } from '@/stores/settings'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { 
  Shield, 
  Key, 
  Smartphone, 
  AlertTriangle, 
  CheckCircle,
  Monitor,
  RefreshCw,
  Eye,
  EyeOff,
  X,
  MapPin,
  Clock
} from 'lucide-react'
import { toast } from 'sonner'

export function SecuritySettings() {
  const { 
    security, 
    changePassword, 
    enableTwoFactor, 
    disableTwoFactor, 
    terminateSession, 
    terminateAllSessions,
    loading 
  } = useSettingsStore()
  
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [twoFactorCode, setTwoFactorCode] = useState('')

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match')
      return
    }
    
    if (passwordForm.new.length < 8) {
      toast.error('Password must be at least 8 characters long')
      return
    }
    
    try {
      await changePassword(passwordForm.current, passwordForm.new)
      setPasswordForm({ current: '', new: '', confirm: '' })
      setShowPasswordForm(false)
      toast.success('Password changed successfully!')
    } catch (error) {
      toast.error('Failed to change password')
    }
  }

  const handleEnableTwoFactor = async () => {
    try {
      const result = await enableTwoFactor()
      setShowTwoFactorSetup(true)
      toast.success('Two-factor authentication enabled!')
    } catch (error) {
      toast.error('Failed to enable 2FA')
    }
  }

  const handleDisableTwoFactor = async () => {
    if (!twoFactorCode.trim()) {
      toast.error('Please enter your 2FA code')
      return
    }
    
    try {
      await disableTwoFactor(twoFactorCode)
      setTwoFactorCode('')
      toast.success('Two-factor authentication disabled')
    } catch (error) {
      toast.error('Failed to disable 2FA')
    }
  }

  const handleTerminateSession = async (sessionId: string) => {
    try {
      await terminateSession(sessionId)
      toast.success('Session terminated')
    } catch (error) {
      toast.error('Failed to terminate session')
    }
  }

  const handleTerminateAllSessions = async () => {
    if (confirm('Are you sure you want to terminate all other sessions?')) {
      try {
        await terminateAllSessions()
        toast.success('All sessions terminated')
      } catch (error) {
        toast.error('Failed to terminate sessions')
      }
    }
  }

  const getPasswordStrength = (password: string) => {
    let strength = 0
    if (password.length >= 8) strength += 1
    if (/[A-Z]/.test(password)) strength += 1
    if (/[a-z]/.test(password)) strength += 1
    if (/[0-9]/.test(password)) strength += 1
    if (/[^A-Za-z0-9]/.test(password)) strength += 1
    
    if (strength <= 2) return { level: 'Weak', color: 'text-red-500' }
    if (strength <= 3) return { level: 'Fair', color: 'text-yellow-500' }
    if (strength <= 4) return { level: 'Good', color: 'text-blue-500' }
    return { level: 'Strong', color: 'text-green-500' }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  return (
    <div className="space-y-6">
      {/* Password Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Key className="h-5 w-5" />
            <span>Password</span>
          </CardTitle>
          <CardDescription>
            Manage your account password and security
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">Password</div>
                <div className="text-sm text-muted-foreground">
                  Last changed: {formatDate(security.password_last_changed)}
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowPasswordForm(!showPasswordForm)}
              >
                Change Password
              </Button>
            </div>

            {showPasswordForm && (
              <form onSubmit={handlePasswordChange} className="space-y-4 p-4 border rounded-lg bg-muted/50">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    >
                      {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.new}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    >
                      {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {passwordForm.new && (
                    <div className="text-sm">
                      Password strength: <span className={getPasswordStrength(passwordForm.new).color}>
                        {getPasswordStrength(passwordForm.new).level}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    >
                      {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowPasswordForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                    Update Password
                  </Button>
                </div>
              </form>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Smartphone className="h-5 w-5" />
            <span>Two-Factor Authentication</span>
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${security.two_factor_enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                <div>
                  <div className="font-medium">Two-Factor Authentication</div>
                  <div className="text-sm text-muted-foreground">
                    {security.two_factor_enabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
              </div>
              {security.two_factor_enabled ? (
                <Button 
                  variant="outline" 
                  onClick={() => setShowTwoFactorSetup(true)}
                  disabled={loading}
                >
                  Disable 2FA
                </Button>
              ) : (
                <Button onClick={handleEnableTwoFactor} disabled={loading}>
                  Enable 2FA
                </Button>
              )}
            </div>

            {showTwoFactorSetup && security.two_factor_enabled && (
              <div className="p-4 border rounded-lg bg-muted/50">
                <h4 className="font-medium mb-3">Disable Two-Factor Authentication</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="2fa-code">Enter your 2FA code</Label>
                    <Input
                      id="2fa-code"
                      placeholder="123456"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      maxLength={6}
                    />
                  </div>
                  <div className="flex items-center justify-end space-x-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowTwoFactorSetup(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={handleDisableTwoFactor} 
                      disabled={loading || !twoFactorCode.trim()}
                    >
                      {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                      Disable 2FA
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {security.two_factor_enabled && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertTitle>Two-Factor Authentication Active</AlertTitle>
                <AlertDescription>
                  Your account is protected with two-factor authentication. You'll need your authenticator app to sign in.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Monitor className="h-5 w-5" />
            <span>Active Sessions</span>
          </CardTitle>
          <CardDescription>
            Manage your active sessions across devices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {security.active_sessions.length > 0 ? (
              <>
                {security.active_sessions.map((session) => (
                  <div key={session.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Monitor className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <div className="font-medium flex items-center space-x-2">
                          <span>{session.device}</span>
                          {session.current && (
                            <Badge variant="outline" className="text-green-600">
                              Current
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center space-x-4">
                          <span className="flex items-center space-x-1">
                            <MapPin className="h-3 w-3" />
                            <span>{session.location}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>Last active: {session.last_active}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    {!session.current && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleTerminateSession(session.id)}
                        disabled={loading}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Terminate
                      </Button>
                    )}
                  </div>
                ))}
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Terminate All Other Sessions</div>
                    <div className="text-sm text-muted-foreground">
                      Sign out from all devices except this one
                    </div>
                  </div>
                  <Button 
                    variant="destructive"
                    onClick={handleTerminateAllSessions}
                    disabled={loading || security.active_sessions.length <= 1}
                  >
                    Terminate All
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Monitor className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold">No Active Sessions</h3>
                <p className="text-muted-foreground">You don't have any other active sessions</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Security Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <span>Security Recommendations</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              {security.two_factor_enabled ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-orange-500" />
              )}
              <div className="flex-1">
                <div className="font-medium">Enable Two-Factor Authentication</div>
                <div className="text-sm text-muted-foreground">
                  Protect your account with an additional security layer
                </div>
              </div>
              {security.two_factor_enabled && (
                <Badge variant="outline" className="text-green-600">
                  Enabled
                </Badge>
              )}
            </div>

            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div className="flex-1">
                <div className="font-medium">Use Strong Password</div>
                <div className="text-sm text-muted-foreground">
                  Your password should be unique and hard to guess
                </div>
              </div>
              <Badge variant="outline" className="text-green-600">
                Good
              </Badge>
            </div>

            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div className="flex-1">
                <div className="font-medium">Regular Password Updates</div>
                <div className="text-sm text-muted-foreground">
                  Change your password regularly for better security
                </div>
              </div>
              <Badge variant="outline" className="text-green-600">
                Recent
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 